import { Component, ElementRef, OnChanges, OnDestroy, OnInit, SimpleChanges, forwardRef, ChangeDetectionStrategy, inject, input, output, viewChild , model} from '@angular/core';

import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectorOption {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  disabled?: boolean;
  data?: any;
}

@Component({
  selector: 'app-searchable-selector',
  host: { '(document:click)': 'onDocumentClick($event)' },
  imports: [FormsModule],
  templateUrl: './searchable-selector.component.html',
  styleUrl: './searchable-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectorComponent),
      multi: true
    }
  ]
})
export class SearchableSelectorComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  private elementRef = inject(ElementRef);

  readonly searchInput = viewChild.required<ElementRef<HTMLInputElement>>('searchInput');

  // Inputs
  readonly options = input<SelectorOption[]>([]);
  readonly placeholder = input<string>('Select an option');
  readonly searchPlaceholder = input<string>('Search...');
  readonly emptyMessage = input<string>('No options found');
  readonly disabled = model<boolean>(false);
  readonly showSearch = input<boolean>(true);
  readonly showIcon = input<boolean>(true);
  readonly clearable = input<boolean>(true);
  readonly multiple = input<boolean>(false);
  readonly maxDisplayItems = input<number>(3);
  readonly dropdownPosition = input<'top' | 'bottom'>('bottom');

  // Outputs
  readonly selectionChange = output<SelectorOption | SelectorOption[] | null>();
  readonly search = output<string>();

  // Internal state
  isOpen = false;
  searchTerm = '';
  filteredOptions: SelectorOption[] = [];
  selectedOption: SelectorOption | null = null;
  selectedOptions: SelectorOption[] = [];
  highlightedIndex = -1;

  // Computed panel position (fixed-positioned to escape stacking/overflow ancestors)
  panelStyle: { top: string; left: string; width: string } = {
    top: '0',
    left: '0',
    width: '0',
  };
  // Resolved direction after measuring available viewport space
  resolvedPosition: 'top' | 'bottom' = 'bottom';
  private readonly onWindowChange = () => {
    if (this.isOpen) this.updatePanelPosition();
  };

  // ControlValueAccessor
  private onChange: (value: any) => void = () => { /* set by registerOnChange */ };
  private onTouched: () => void = () => { /* set by registerOnTouched */ };

  ngOnInit(): void {
    this.filteredOptions = [...this.options()];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.filterOptions();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onWindowChange, true);
    window.removeEventListener('resize', this.onWindowChange);
  }

  private updatePanelPosition(): void {
    const trigger: HTMLElement | null = this.elementRef.nativeElement.querySelector('.selector-trigger');
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const gap = 4;

    // Default behavior is to open below the trigger, matching native <select>.
    // Only place the panel above when the caller explicitly opts in via the
    // `dropdownPosition` input — the dropdown's own .dropdown-options has
    // max-height + internal scrolling, so a long list doesn't need an
    // auto-flip to remain usable.
    const placeAbove = this.dropdownPosition() === 'top';
    this.resolvedPosition = placeAbove ? 'top' : 'bottom';

    const panelMaxHeight = 360;
    const top = placeAbove
      ? Math.max(8, rect.top - gap - panelMaxHeight)
      : rect.bottom + gap;

    this.panelStyle = {
      top: `${top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    };
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (this.multiple()) {
      if (Array.isArray(value)) {
        this.selectedOptions = this.options().filter(opt => value.includes(opt.id));
      } else {
        this.selectedOptions = [];
      }
    } else {
      if (value) {
        this.selectedOption = this.options().find(opt => opt.id === value) || null;
      } else {
        this.selectedOption = null;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Getters
  get displayValue(): string {
    if (this.multiple()) {
      if (this.selectedOptions.length === 0) return '';
      if (this.selectedOptions.length <= this.maxDisplayItems()) {
        return this.selectedOptions.map(o => o.label).join(', ');
      }
      return `${this.selectedOptions.slice(0, this.maxDisplayItems()).map(o => o.label).join(', ')} +${this.selectedOptions.length - this.maxDisplayItems()} more`;
    }
    return this.selectedOption?.label || '';
  }

  get hasSelection(): boolean {
    return this.multiple() ? this.selectedOptions.length > 0 : !!this.selectedOption;
  }

  // Actions
  toggleDropdown(): void {
    if (this.disabled()) return;
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
    this.onTouched();
  }

  openDropdown(): void {
    if (this.disabled() || this.isOpen) return;
    this.isOpen = true;
    this.searchTerm = '';
    this.filterOptions();
    this.highlightedIndex = -1;
    this.updatePanelPosition();
    window.addEventListener('scroll', this.onWindowChange, true);
    window.addEventListener('resize', this.onWindowChange);
    setTimeout(() => this.searchInput()?.nativeElement?.focus(), 0);
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.searchTerm = '';
    this.highlightedIndex = -1;
    window.removeEventListener('scroll', this.onWindowChange, true);
    window.removeEventListener('resize', this.onWindowChange);
  }

  selectOption(option: SelectorOption): void {
    if (option.disabled) return;

    if (this.multiple()) {
      const index = this.selectedOptions.findIndex(o => o.id === option.id);
      if (index >= 0) {
        this.selectedOptions.splice(index, 1);
      } else {
        this.selectedOptions = [...this.selectedOptions, option];
      }
      this.onChange(this.selectedOptions.map(o => o.id));
      this.selectionChange.emit([...this.selectedOptions]);
    } else {
      this.selectedOption = option;
      this.onChange(option.id);
      this.selectionChange.emit(option);
      this.closeDropdown();
    }
  }

  clearSelection(event?: Event): void {
    event?.stopPropagation();
    if (this.multiple()) {
      this.selectedOptions = [];
      this.onChange([]);
      this.selectionChange.emit([]);
    } else {
      this.selectedOption = null;
      this.onChange(null);
      this.selectionChange.emit(null);
    }
  }

  removeOption(option: SelectorOption, event: Event): void {
    event.stopPropagation();
    if (this.multiple()) {
      this.selectedOptions = this.selectedOptions.filter(o => o.id !== option.id);
      this.onChange(this.selectedOptions.map(o => o.id));
      this.selectionChange.emit([...this.selectedOptions]);
    }
  }

  isSelected(option: SelectorOption): boolean {
    if (this.multiple()) {
      return this.selectedOptions.some(o => o.id === option.id);
    }
    return this.selectedOption?.id === option.id;
  }

  onSearchChange(): void {
    this.filterOptions();
    this.highlightedIndex = this.filteredOptions.length > 0 ? 0 : -1;
    this.search.emit(this.searchTerm);
  }

  filterOptions(): void {
    if (!this.searchTerm.trim()) {
      this.filteredOptions = [...this.options()];
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredOptions = this.options().filter(option =>
        option.label.toLowerCase().includes(term) ||
        option.description?.toLowerCase().includes(term)
      );
    }
  }

  // Keyboard navigation
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.openDropdown();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightNext();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightPrevious();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length) {
          this.selectOption(this.filteredOptions[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
      case 'Tab':
        this.closeDropdown();
        break;
    }
  }

  highlightNext(): void {
    const enabledOptions = this.filteredOptions.filter(o => !o.disabled);
    if (enabledOptions.length === 0) return;

    let nextIndex = this.highlightedIndex + 1;
    while (nextIndex < this.filteredOptions.length && this.filteredOptions[nextIndex].disabled) {
      nextIndex++;
    }
    if (nextIndex < this.filteredOptions.length) {
      this.highlightedIndex = nextIndex;
      this.scrollToHighlighted();
    }
  }

  highlightPrevious(): void {
    let prevIndex = this.highlightedIndex - 1;
    while (prevIndex >= 0 && this.filteredOptions[prevIndex].disabled) {
      prevIndex--;
    }
    if (prevIndex >= 0) {
      this.highlightedIndex = prevIndex;
      this.scrollToHighlighted();
    }
  }

  scrollToHighlighted(): void {
    setTimeout(() => {
      const dropdown = this.elementRef.nativeElement.querySelector('.dropdown-options');
      const highlighted = dropdown?.querySelector('.option.highlighted');
      if (highlighted && dropdown) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    }, 0);
  }

  // Click outside to close
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }
}